import { useState, useEffect } from "react";
import { useAuth, API } from "../App";
import { Link } from "react-router-dom";
import axios from "axios";
import {
  Building2,
  Users,
  DollarSign,
  AlertTriangle,
  Wrench,
  Plus,
  ArrowRight,
  FileText,
  Receipt,
  Clock,
  CheckCircle2
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";

const Dashboard = () => {
  const { token, user } = useAuth();
  const [stats, setStats] = useState(null);
  const [lateRents, setLateRents] = useState([]);
  const [renewals, setRenewals] = useState([]);
  const [workOrders, setWorkOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsRes, lateRes, workRes, renewalRes] = await Promise.all([
          axios.get(`${API}/dashboard/stats`, { headers }),
          axios.get(`${API}/rent-payments/late`, { headers }),
          axios.get(`${API}/work-orders`, { headers }),
          axios.get(`${API}/leases/renewal-reminders?days_ahead=30`, { headers }).catch(() => ({ data: [] }))
        ]);
        
        setStats(statsRes.data);
        setLateRents(lateRes.data.slice(0, 3));
        setWorkOrders(workRes.data.filter(w => w.status !== "completed" && w.status !== "cancelled").slice(0, 3));
        setRenewals(renewalRes.data.slice(0, 3));
      } catch (error) {
        console.error("Dashboard error:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [token]);

  if (loading) {
    return (
      <div className="space-y-6 animate-pulse">
        <div className="h-8 bg-slate-200 rounded w-48" />
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => <div key={i} className="h-24 bg-slate-200 rounded-xl" />)}
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="h-64 bg-slate-200 rounded-xl" />
          <div className="h-64 bg-slate-200 rounded-xl" />
        </div>
      </div>
    );
  }

  // Quick actions - big touch targets
  const quickActions = [
    { icon: Plus, label: "Add Property", to: "/properties", color: "bg-green-600 hover:bg-green-700" },
    { icon: Users, label: "Add Tenant", to: "/tenants", color: "bg-blue-600 hover:bg-blue-700" },
    { icon: DollarSign, label: "Record Payment", to: "/rent-payments", color: "bg-emerald-600 hover:bg-emerald-700" },
    { icon: Wrench, label: "New Work Order", to: "/work-orders", color: "bg-orange-600 hover:bg-orange-700" },
  ];

  // Count items needing attention
  const attentionCount = lateRents.length + workOrders.length + renewals.length;

  return (
    <div className="space-y-6" data-testid="dashboard">
      {/* Welcome */}
      <div>
        <h1 className="text-2xl font-bold text-slate-900">
          Hello, {user?.name?.split(' ')[0]}
        </h1>
        <p className="text-slate-500 mt-1">
          {attentionCount > 0 
            ? `${attentionCount} item${attentionCount > 1 ? 's' : ''} need your attention`
            : "Everything looks good today"
          }
        </p>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Link to="/properties">
          <Card className="hover:shadow-md transition-shadow cursor-pointer">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Properties</p>
                  <p className="text-2xl font-bold text-slate-900">{stats?.total_properties || 0}</p>
                </div>
                <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                  <Building2 className="w-6 h-6 text-green-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </Link>

        <Link to="/tenants">
          <Card className="hover:shadow-md transition-shadow cursor-pointer">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Tenants</p>
                  <p className="text-2xl font-bold text-slate-900">{stats?.total_tenants || 0}</p>
                </div>
                <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                  <Users className="w-6 h-6 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </Link>

        <Link to="/rent-payments">
          <Card className="hover:shadow-md transition-shadow cursor-pointer">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">This Month</p>
                  <p className="text-2xl font-bold text-emerald-600">${(stats?.total_revenue || 0).toLocaleString()}</p>
                </div>
                <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center">
                  <DollarSign className="w-6 h-6 text-emerald-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </Link>

        <Link to="/leases">
          <Card className="hover:shadow-md transition-shadow cursor-pointer">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Active Leases</p>
                  <p className="text-2xl font-bold text-slate-900">{stats?.active_leases || 0}</p>
                </div>
                <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                  <FileText className="w-6 h-6 text-purple-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </Link>
      </div>

      {/* Quick Actions */}
      <div>
        <h2 className="text-lg font-semibold text-slate-900 mb-3">Quick Actions</h2>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {quickActions.map((action, i) => (
            <Link key={i} to={action.to}>
              <Button 
                className={`w-full h-14 text-base font-medium ${action.color} text-white`}
                size="lg"
              >
                <action.icon className="w-5 h-5 mr-2" />
                {action.label}
              </Button>
            </Link>
          ))}
        </div>
      </div>

      {/* Attention Items */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Late Rent */}
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base font-semibold flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-red-500" />
                Late Rent
                {lateRents.length > 0 && (
                  <Badge variant="destructive" className="ml-2">{lateRents.length}</Badge>
                )}
              </CardTitle>
              <Link to="/rent-payments">
                <Button variant="ghost" size="sm">View all</Button>
              </Link>
            </div>
          </CardHeader>
          <CardContent>
            {lateRents.length === 0 ? (
              <div className="text-center py-6">
                <CheckCircle2 className="w-10 h-10 text-green-500 mx-auto mb-2" />
                <p className="text-slate-500">All rents collected!</p>
              </div>
            ) : (
              <div className="space-y-3">
                {lateRents.map((item, i) => (
                  <div key={i} className="flex items-center justify-between p-3 bg-red-50 rounded-lg">
                    <div className="min-w-0 flex-1">
                      <p className="font-medium text-slate-900 truncate">{item.tenant?.name}</p>
                      <p className="text-sm text-slate-500 truncate">{item.property?.address}</p>
                    </div>
                    <div className="text-right ml-3">
                      <p className="font-bold text-red-600">${item.amount_due?.toLocaleString()}</p>
                      <p className="text-xs text-red-500">{item.days_late}d late</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Work Orders */}
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base font-semibold flex items-center gap-2">
                <Wrench className="w-5 h-5 text-orange-500" />
                Open Maintenance
                {workOrders.length > 0 && (
                  <Badge className="ml-2 bg-orange-100 text-orange-700">{workOrders.length}</Badge>
                )}
              </CardTitle>
              <Link to="/work-orders">
                <Button variant="ghost" size="sm">View all</Button>
              </Link>
            </div>
          </CardHeader>
          <CardContent>
            {workOrders.length === 0 ? (
              <div className="text-center py-6">
                <CheckCircle2 className="w-10 h-10 text-green-500 mx-auto mb-2" />
                <p className="text-slate-500">No open work orders</p>
              </div>
            ) : (
              <div className="space-y-3">
                {workOrders.map((wo) => (
                  <div key={wo.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                    <div className="min-w-0 flex-1">
                      <p className="font-medium text-slate-900 truncate">{wo.title}</p>
                      <p className="text-sm text-slate-500 truncate">{wo.property?.address}</p>
                    </div>
                    <Badge className={
                      wo.priority === "urgent" ? "bg-red-100 text-red-700" :
                      wo.priority === "high" ? "bg-orange-100 text-orange-700" :
                      "bg-slate-100 text-slate-600"
                    }>
                      {wo.priority}
                    </Badge>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Expiring Leases */}
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base font-semibold flex items-center gap-2">
                <Clock className="w-5 h-5 text-amber-500" />
                Expiring Soon
                {renewals.length > 0 && (
                  <Badge className="ml-2 bg-amber-100 text-amber-700">{renewals.length}</Badge>
                )}
              </CardTitle>
              <Link to="/leases">
                <Button variant="ghost" size="sm">View all</Button>
              </Link>
            </div>
          </CardHeader>
          <CardContent>
            {renewals.length === 0 ? (
              <div className="text-center py-6">
                <CheckCircle2 className="w-10 h-10 text-green-500 mx-auto mb-2" />
                <p className="text-slate-500">No leases expiring soon</p>
              </div>
            ) : (
              <div className="space-y-3">
                {renewals.map((r, i) => (
                  <div key={i} className="flex items-center justify-between p-3 bg-amber-50 rounded-lg">
                    <div className="min-w-0 flex-1">
                      <p className="font-medium text-slate-900 truncate">{r.tenant_name}</p>
                      <p className="text-sm text-slate-500 truncate">{r.property_address}</p>
                    </div>
                    <div className="text-right ml-3">
                      <p className="text-sm font-medium text-amber-700">{r.days_remaining}d left</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Pending Invoices */}
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base font-semibold flex items-center gap-2">
                <Receipt className="w-5 h-5 text-blue-500" />
                Pending Invoices
                {(stats?.pending_invoices || 0) > 0 && (
                  <Badge className="ml-2 bg-blue-100 text-blue-700">{stats?.pending_invoices}</Badge>
                )}
              </CardTitle>
              <Link to="/invoices">
                <Button variant="ghost" size="sm">View all</Button>
              </Link>
            </div>
          </CardHeader>
          <CardContent>
            {(stats?.pending_invoices || 0) === 0 ? (
              <div className="text-center py-6">
                <CheckCircle2 className="w-10 h-10 text-green-500 mx-auto mb-2" />
                <p className="text-slate-500">All invoices sent</p>
              </div>
            ) : (
              <div className="text-center py-6">
                <p className="text-3xl font-bold text-blue-600">{stats?.pending_invoices}</p>
                <p className="text-slate-500 mt-1">invoices waiting to be sent</p>
                <Link to="/invoices">
                  <Button className="mt-4" variant="outline">
                    Review Invoices <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Dashboard;
